import { useState } from 'react';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { TableInput } from '@round/whitelabel-ui';
import { numberWithCommas, showNotification } from 'helpers';
import { PostsTableRow } from '../../helpers';

const ERROR_MSG = 'Failed to update estimated views';

type Row = PostsTableRow;

type Context<T extends Row> = CellContext<T, number | null | undefined>;

const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

const EstimatedViewsCell = <T extends Row>({ table, row }: Context<T>) => {
    // Read the post's own value from the row rather than getValue(): the column accessor carries
    // the account-average fallback, and this cell needs to tell a stored 0 apart from "no estimate".
    const postEstimatedViews = row.original.estimated_views;
    const averagePostViews = 'userStatsCache' in row.original ? row.original.userStatsCache?.avg_post_views : null;

    const initialValue = (postEstimatedViews ?? averagePostViews ?? '').toString();
    const [estimatedViews, setEstimatedViews] = useState<string>(initialValue);

    const [isInputFocused, setIsInputFocused] = useState(false);

    const { isLoading, getIsAccountDataLoading, updatePost } = getTableMeta(table);

    const handleUpdate = async (value: number | null) => {
        if (!updatePost) {
            return;
        }

        // Nothing changed (same as the stored value, or the fallback shown for a post with no stored
        // value), or the value is negative and the API field is non-negative, so it would be rejected.
        if (
            value === postEstimatedViews ||
            (postEstimatedViews === null && value === (averagePostViews ?? null)) ||
            (value !== null && value < 0)
        ) {
            setEstimatedViews(initialValue);
            return;
        }

        try {
            const response = await updatePost(row.original.id, {
                estimated_views: value,
            });

            if (response.status === 200) {
                setEstimatedViews(response.data.estimated_views?.toString() || averagePostViews?.toString() || '');
                showNotification('Estimated views updated successfully', 'info');
                return;
            }

            showNotification(ERROR_MSG, 'error');
            setEstimatedViews(initialValue);
        } catch {
            showNotification(ERROR_MSG, 'error');
            setEstimatedViews(initialValue);
        }
    };

    const getBlurredValue = (value: string) => {
        return value === '' ? '-' : numberWithCommas(value);
    };

    if (isLoading || getIsAccountDataLoading(row.original)) {
        return <Skeleton />;
    }

    return (
        <TableInput
            type={isInputFocused ? 'number' : 'text'}
            min={0}
            value={isInputFocused ? estimatedViews : getBlurredValue(estimatedViews)}
            onFocus={() => setIsInputFocused(true)}
            onChange={(e) => setEstimatedViews(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.blur();
                }
            }}
            onBlur={(e) => {
                let parsedValue = e.target.value ? parseInt(e.target.value) : null;
                handleUpdate(parsedValue);
                setIsInputFocused(false);
            }}
        />
    );
};

export default EstimatedViewsCell;
